import { useState, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { css } from "@emotion/react";
import { CometChat } from "@cometchat-pro/chat";
import InfiniteScroll from "react-infinite-scroller";

// custom
import { useChatSentMutation } from "graphql/generated";
import MessageListHeader from "components/chat/shared/MessageListHeader";
import MessageList from "components/chat/shared/MessageList";
import SendInput from "components/chat/shared/SendInput";
import EditInput from "components/chat/shared/EditInput";

// mui
import Box from "@mui/material/Box";

const style = {
  root: css`
    display: flex;
    flex-direction: column;
    height: 100%;
  `,
  msgs: css`
    flex: 1 1 0;
    overflow-y: auto;
  `,
};

type MessagesProps = {
  group: CometChat.Group;
};
const Messages: React.FC<MessagesProps> = (props) => {
  const { group } = props;
  const [msgs, setMsgs] = useState<CometChat.BaseMessage[]>([]);
  // 同じobjectに対してfetchPreviousを呼ぶことで勝手にpaginationしてくれる
  const [messagesRequest, setMessagesRequest] = useState<CometChat.MessagesRequest | null>(null);

  const [editingMsg, setEditingMsg] = useState<CometChat.TextMessage | null>(null);

  const [, msgSent] = useChatSentMutation();

  // event handlerの中でも、現在の状態を参照できるようにする
  const msgsRef = useRef(msgs);
  const setMsgsRef = (data: CometChat.BaseMessage[]) => {
    msgsRef.current = data;
    setMsgs(data);
  };

  // 既読処理
  // https://www.cometchat.com/docs/javascript-chat-sdk/messaging-receipts#mark-messages-as-read
  const markMsgAsRead = (lastMsg: CometChat.BaseMessage) => {
    CometChat.markAsRead(lastMsg.getId(), group.getGuid(), "group", lastMsg.getSender().getUid());
  };

  // https://www.cometchat.com/docs/javascript-chat-sdk/messaging-receive-message
  // 最初のメッセージの取得
  const fetchMsgs = (req: CometChat.MessagesRequest) => {
    req.fetchPrevious().then(
      (msgs: CometChat.BaseMessage[]) => {
        console.log("aaa: Message list fetched:", msgs);
        setMsgsRef(msgs);

        // 既読処理
        const lastMsg = msgs[msgs.length - 1];
        markMsgAsRead(lastMsg);
      },
      (error: any) => {
        console.log("Message fetching failed with error:", error);
      },
    );
  };

  // infinite scroll時にもっと読み込むとき
  const fetchMsgsMore = () => {
    if (messagesRequest === null) {
      return;
    }
    messagesRequest.fetchPrevious().then(
      (msgs: CometChat.BaseMessage[]) => {
        console.log("bbb: Message list fetched:", msgs);
        setMsgsRef([...msgs, ...msgsRef.current]);
      },
      (error: any) => {
        console.log("Message fetching failed with error:", error);
      },
    );
  };

  // メッセージを受信したときのハンドラーの登録/解除
  const receivedMsg = () => {
    const guid = group.getGuid();
    const listenerID = uuidv4();
    const shouldReact = (msg: CometChat.BaseMessage) => {
      return msg.getReceiverId() === guid;
    };
    const listener = new CometChat.MessageListener({
      onTextMessageReceived: (textMessage: CometChat.TextMessage) => {
        console.log("Text message received successfully", textMessage);
        if (!shouldReact(textMessage)) {
          return;
        }
        setMsgsRef([...msgsRef.current, textMessage]);
        markMsgAsRead(textMessage);
      },
      onMediaMessageReceived: (mediaMessage: CometChat.MediaMessage) => {
        console.log("Media message received successfully", mediaMessage);
        if (!shouldReact(mediaMessage)) {
          return;
        }
        setMsgsRef([...msgsRef.current, mediaMessage]);
        markMsgAsRead(mediaMessage);
      },
      // NOTE: 本当はもっといっぱいある
      // https://www.cometchat.com/docs/javascript-chat-sdk/resources-all-real-time-listeners#message-listener
    });
    return {
      listenerID,
      listener,
    };
  };

  // propsで渡されてくるguidが変わったら
  // 1.メッセージの取得
  // 2.受信メッセージのリスナー登録
  useEffect(() => {
    // requestのobjectを作り変える
    const msgsReq = new CometChat.MessagesRequestBuilder().setGUID(group.getGuid()).setLimit(30).build();
    setMessagesRequest(msgsReq);
    // 1.メッセージの取得
    fetchMsgs(msgsReq);

    // 2.受信メッセージのリスナー登録
    const receivedMsgHandler = receivedMsg();
    CometChat.addMessageListener(receivedMsgHandler.listenerID, receivedMsgHandler.listener);
    return () => CometChat.removeMessageListener(receivedMsgHandler.listenerID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.getGuid()]);

  // 送信された後に実行すべき処理
  // As a sender, you will not receive your own message in a real-time message event.
  // However, if a user is logged-in using multiple devices, they will receive an event for their own message in other devices.
  const handleMsgSent = (
    msg: CometChat.TextMessage | CometChat.MediaMessage | CometChat.CustomMessage | CometChat.BaseMessage,
  ) => {
    setMsgsRef([...msgs, msg]);
    const sender = msg.getSender();
    const receiver = msg.getReceiver() as CometChat.Group;

    msgSent({ senderUid: sender.getUid(), guid: receiver.getGuid(), groupName: receiver.getName() });
  };

  // メッセージの編集をした後にすべき処理
  const handleMsgUpdate = (updatedMsg: CometChat.BaseMessage) => {
    const newMsgs = msgs.map((m) => {
      if (m.getId() === updatedMsg.getId()) {
        return updatedMsg;
      }
      return m;
    });
    setMsgsRef(newMsgs);
    setEditingMsg(null);
  };

  // メッセージの削除
  // https://www.cometchat.com/docs/javascript-chat-sdk/messaging-delete-message
  const handleMsgDelete = (deletingMsg: CometChat.BaseMessage) => {
    CometChat.deleteMessage(deletingMsg.getId().toString()).then(
      (deletedMsg: CometChat.BaseMessage) => {
        const newMsgs = msgs.map((m) => {
          if (m.getId() === deletedMsg.getId()) {
            return deletedMsg;
          }
          return m;
        });
        setMsgsRef(newMsgs);
      },
      (error: any) => {
        console.log("Message delete failed with error:", error);
      },
    );
  };

  return (
    <Box css={style.root}>
      <MessageListHeader group={group} />
      <Box css={style.msgs}>
        <InfiniteScroll
          pageStart={0}
          useWindow={false}
          initialLoad={false}
          loadMore={fetchMsgsMore}
          hasMore={true}
          isReverse={true}
        >
          <MessageList msgs={msgs} handleMsgEdit={setEditingMsg} handleMsgDelete={handleMsgDelete} />
        </InfiniteScroll>
      </Box>
      {editingMsg === null && <SendInput guid={group.getGuid()} handleMsgSent={handleMsgSent} />}
      {editingMsg !== null && <EditInput guid={group.getGuid()} textMsg={editingMsg} handleMsgUpdate={handleMsgUpdate} />}
    </Box>
  );
};

export default Messages;
